import { useQuery, useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import { useEffect, useRef } from 'react';

import { VolumeResponse, VolumeCountResponse } from '~/shared/types/api';
import { SelectOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { getApiFilters } from '~/shared/utils/apiFilters';
import { TABLE_LIVE_REFRESH_MS } from '~/shared/constants/polling';

import { canBeDatafied, datafyErrorToast } from './helpers';

const baseUrl = `/api/v1/volumes`;

export interface UseVolumesQueryParams {
  qs: URLSearchParams;
  filters: SelectOption[];
  sortField: { field: string; dir: 'desc' | 'asc' };
  pageOffset: string;
  savingsChartQueryKey?: string;
  utilizationChartQueryKey?: string;
  datafySwitchVolumes: Array<{ volumeId?: string; state?: string }> | undefined;
  removeDatafySwitchVolume: (id: string) => void;
}

export function useVolumesQuery({
  qs,
  filters,
  sortField,
  pageOffset,
  savingsChartQueryKey,
  utilizationChartQueryKey,
  datafySwitchVolumes,
  removeDatafySwitchVolume,
}: UseVolumesQueryParams) {
  const api = useXFetch();
  const queryClient = useQueryClient();
  const previousStatusesRef = useRef<{ monitoring: string; volumeId: string }[]>([]);

  const query = useQuery<{ volumes: VolumeResponse['volumes']; count: number }>({
    queryKey: [baseUrl, JSON.stringify({ filters, sortField, pageOffset })],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const localQs = new URLSearchParams(qs.toString());
      const processedApiFiltersResult = getApiFilters(filters);
      const processedApiFilters = Array.isArray(processedApiFiltersResult)
        ? processedApiFiltersResult
        : [processedApiFiltersResult];
      processedApiFilters.forEach((filterValue: string) => {
        const currentValues = localQs.getAll('filters');
        if (!currentValues.includes(filterValue)) {
          localQs.append('filters', filterValue);
        }
      });
      const volumes = await api<VolumeResponse>(`${baseUrl}?${localQs.toString()}`);
      const { count } = await api<VolumeCountResponse>(`${baseUrl}/count?${localQs.toString()}`);
      return { volumes: volumes.volumes, count: count ?? 0 };
    },
    refetchInterval: TABLE_LIVE_REFRESH_MS,
  });

  useEffect(() => {
    const data = query.data;
    if (!data) {
      return;
    }
    const currentStatuses = (data.volumes || []).map(({ monitoring, volumeId }) => ({
      monitoring: monitoring!,
      volumeId: volumeId!,
    }));
    const previousStatuses = previousStatusesRef.current;
    if (previousStatuses.length && !isEqual(previousStatuses, currentStatuses)) {
      const changedStatuses = previousStatuses.filter((prev) => {
        const currentStatus = currentStatuses.find((s) => s.volumeId === prev.volumeId);
        return currentStatus && currentStatus.monitoring !== prev.monitoring;
      });
      changedStatuses.forEach((prevStatus) => {
        const newStatus = currentStatuses.find((curr) => curr.volumeId === prevStatus.volumeId);
        const machineVolume = datafySwitchVolumes?.find((v) => v.volumeId === prevStatus.volumeId);
        if (newStatus) {
          if (!newStatus?.monitoring.endsWith('ing')) {
            if (savingsChartQueryKey) {
              queryClient.refetchQueries({ queryKey: [savingsChartQueryKey] });
            }
            if (utilizationChartQueryKey) {
              queryClient.refetchQueries({ queryKey: [utilizationChartQueryKey] });
            }
          }
          const datafyProcessFailed =
            !machineVolume &&
            ((newStatus?.monitoring === 'Monitored' && prevStatus.monitoring === 'Migrating') ||
              (newStatus?.monitoring === 'Managed' && prevStatus.monitoring === 'Unmigrating'));
          if (datafyProcessFailed) {
            datafyErrorToast(
              newStatus.volumeId,
              newStatus?.monitoring === 'Monitored' ? 'datafying' : 'undatafying'
            );
          }
          const datafyProcessSucceeded =
            (machineVolume?.state === 'datafying' && newStatus?.monitoring === 'Managed') ||
            (machineVolume?.state === 'undatafying' && newStatus?.monitoring === 'Monitored') ||
            (machineVolume?.volumeId &&
              ((newStatus?.monitoring === 'Migrating' && prevStatus.monitoring === 'Monitored') ||
                (newStatus?.monitoring === 'Unmigrating' && prevStatus.monitoring === 'Managed')));
          if (datafyProcessSucceeded) {
            removeDatafySwitchVolume(newStatus?.volumeId);
          }
        }
      });
    }
    previousStatusesRef.current = currentStatuses;
  }, [
    query.data,
    datafySwitchVolumes,
    removeDatafySwitchVolume,
    savingsChartQueryKey,
    utilizationChartQueryKey,
    queryClient,
  ]);

  return query;
}

export function getCanBeDatafiedVolumeIds(volumes?: VolumeResponse['volumes']) {
  return (volumes || []).filter(canBeDatafied).map((v) => v.volumeId!);
}
